import {Box, Flex, Center, Button, useBreakpointValue} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {useOcapiInfiniteContentSearch} from '../../hooks/useOcapi'
import {Skeleton as AddToCartSkeleton} from '../../components/add-to-cart-cta'
import WidgetArticleDefaultViewMobile from './widget-article-default-view-mobile'
import WidgetArticleDefaultViewDesktop from './widget-article-default-view-desktop'
import ContentTile from '../../components/content-tile'
import {FormattedMessage, useIntl} from 'react-intl'
import Slider from '../../components/slider'
import useIntersectionObserver from '../../hooks/use-intersection-observer'

/**
 * Display items from content_search
 *
 * @param {string} folder - folder name
 * @param {string} count - number of results
 * @param {string} type - default, grid or slider, defines which layout is choosen
 * @param {string} q - searchquery
 * @param {string} showLoadMore - show or hide "load more" button, only effects if type=grid
 * @param {'lazy'|'eager'} [loading] - Lazy load the widget or render it immediately
 *
 * @example
 *   <pwa-widget-article folder="stokke-stories" count="3" type="default">
 *     $include(...)$
 *   </pwa-widget-article>
 */
const WidgetArticle = ({
    folder,
    q,
    type,
    count: countStr,
    showLoadMore: showLoadMoreStr,
    loading = 'lazy'
}) => {
    // flag that indicates if the widget was displayed to trigger loading of data
    const [setRef, wasDisplayed] = useIntersectionObserver({
        useOnce: true,
        initialInView: loading === 'eager',
        skip: loading === 'eager'
    })
    const {formatMessage} = useIntl()
    const count = Number.parseInt(countStr, 10)
    const showLoadMore = showLoadMoreStr && showLoadMoreStr !== 'false'
    const smallOrBigView = useBreakpointValue({base: 'small', lg: 'big'})

    const isMobile = useBreakpointValue({base: true, md: false})
    let displayType = type
    if (!isMobile && type === 'slider') {
        displayType = 'grid'
    }

    const {data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage} =
        useOcapiInfiniteContentSearch(
            {
                parameters: {
                    queryString: q,
                    fdid: folder,
                    count: count,
                    ...(folder && {sortAttribute: 'storiesPriority'})
                }
            },
            {
                enabled: !!wasDisplayed,
                select: (data) => data?.pages?.flatMap(({hits}) => hits)
            }
        )

    const slicedData = useMemo(() => data?.slice(0, 8), [data])

    return (
        <Box ref={setRef}>
            {/* TODO: display a more correct skeleton that doesn't generate layout shift */}
            {isLoading && <AddToCartSkeleton />}
            {data?.length > 1 && (
                <>
                    {displayType === 'grid' ? (
                        <Box data-testid="grid-element">
                            <Flex
                                flexWrap="wrap"
                                justifyContent={['center', 'flex-start']}
                                gap={['0', '24px']}
                                padding={['16px 32px 8px 32px', '16px 32px 32px 32px']}
                            >
                                {data.map((item) => (
                                    <Box
                                        width={['100%', 'calc(100% / 3 - 17px)']}
                                        paddingBottom={['24px', '0']}
                                        key={item.id}
                                    >
                                        <ContentTile
                                            content={item}
                                            variant="default"
                                            ratio={4 / 3}
                                        />
                                    </Box>
                                ))}
                            </Flex>
                            {isFetchingNextPage && <AddToCartSkeleton />}
                            {showLoadMore && hasNextPage && (
                                <Center padding="2.5rem 1rem">
                                    <Button
                                        variant="outline"
                                        width={['full', '25rem']}
                                        data-testid="more-button"
                                        onClick={() => fetchNextPage()}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Load more"
                                            id="global.loadmore"
                                        />
                                    </Button>
                                </Center>
                            )}
                        </Box>
                    ) : displayType === 'slider' ? (
                        <Box data-testid="slider-element" padding="32px 16px">
                            <Slider
                                itemsPerSlide={1.5}
                                label={formatMessage({
                                    defaultMessage: 'Article carousel',
                                    id: 'widget.article.slider.assistive_msg.label'
                                })}
                            >
                                {slicedData?.map((item) => (
                                    <Box key={item.id}>
                                        <ContentTile content={item} />
                                    </Box>
                                ))}
                            </Slider>
                        </Box>
                    ) : (
                        <Box data-testid="default-element">
                            {smallOrBigView === 'small' ? (
                                <WidgetArticleDefaultViewMobile sliderData={data} />
                            ) : (
                                <WidgetArticleDefaultViewDesktop sliderData={data} />
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

WidgetArticle.propTypes = {
    folder: PropTypes.string,
    q: PropTypes.string,
    type: PropTypes.string,
    count: PropTypes.string,
    showLoadMore: PropTypes.string,
    loading: PropTypes.oneOf(['lazy', 'eager'])
}

export default WidgetArticle
